import * as React from "react";
import { skipToken } from "@reduxjs/toolkit/query";

import { ConnectProto } from "@racoon/api";
import { cn, Tabs, TabsList, TabsTrigger } from "@racoon/ui";

import { useListJobsQuery } from "~/shared/api/jobs/job.grpc";
import { compareIds } from "~/shared/lib/compare-ids";
import { jobStatusTone } from "~/features/map-view/model/constants/job-status-colors";
import { orderAttemptsQuery } from "../model/outcome-correction";

/** Figma: the order's attempts as pills over the report; a single attempt shows no row. */
export const OrderAttempts: React.FC<{
  job: ConnectProto.Job;
  children: (attempt: ConnectProto.Job) => React.ReactNode;
}> = ({ job, children }) => {
  const [selectedId, setSelectedId] = React.useState(String(job.id));
  const { data } = useListJobsQuery(job.orderNumber ? orderAttemptsQuery(job) : skipToken);
  const attempts = [...(data?.jobs ?? [])].sort((a, b) => compareIds(a.id, b.id));
  // The opened job stays live; another attempt reads as the list has it
  const attempt =
    selectedId === String(job.id)
      ? job
      : (attempts.find((other) => String(other.id) === selectedId) ?? job);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {attempts.length > 1 && (
        <div className="border-t border-border-primary p-4">
          <Tabs value={selectedId} onValueChange={setSelectedId}>
            <TabsList className="self-start">
              {attempts.map((other) => (
                <TabsTrigger
                  key={String(other.id)}
                  value={String(other.id)}
                  data-testid={`order-attempt-${String(other.id)}`}
                  className="gap-2"
                >
                  <span
                    className={cn(
                      "size-1.5 rounded-full",
                      jobStatusTone(other.status)?.bg ?? "bg-text-subtle",
                    )}
                  />
                  Z{String(other.id)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      )}
      <React.Fragment key={String(attempt.id)}>{children(attempt)}</React.Fragment>
    </div>
  );
};
